import { create } from 'zustand';
import { api } from '../api/client';

export interface Notification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isOpen: boolean;
  filter: 'all' | 'unread';
  fetch: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: number) => Promise<void>;
  setFilter: (filter: 'all' | 'unread') => void;
  toggle: () => void;
  close: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  filter: 'all',

  fetch: async () => {
    try {
      const res = await api<{ notifications: Notification[]; unread_count: number }>('/console/notifications');
      set({ notifications: res.notifications, unreadCount: res.unread_count });
    } catch { /* ignore polling errors */ }
  },

  markRead: async (id) => {
    await api(`/console/notifications/${id}/read`, { method: 'PATCH' });
    set((s) => ({
      notifications: s.notifications.map(n => n.id === id ? { ...n, is_read: true } : n),
      unreadCount: Math.max(0, s.unreadCount - 1),
    }));
  },

  markAllRead: async () => {
    await api('/console/notifications/read-all', { method: 'POST' });
    set((s) => ({
      notifications: s.notifications.map(n => ({ ...n, is_read: true })),
      unreadCount: 0,
    }));
  },

  remove: async (id) => {
    await api(`/console/notifications/${id}`, { method: 'DELETE' });
    set((s) => {
      const target = s.notifications.find(n => n.id === id);
      return {
        notifications: s.notifications.filter(n => n.id !== id),
        unreadCount: target && !target.is_read ? Math.max(0, s.unreadCount - 1) : s.unreadCount,
      };
    });
  },

  setFilter: (filter) => set({ filter }),

  toggle: () => {
    const wasOpen = get().isOpen;
    set({ isOpen: !wasOpen });
    if (!wasOpen) get().fetch();
  },

  close: () => set({ isOpen: false }),
}));
